import type React from 'react';
import {cn} from '../../../../../lib/classname';

type Tag = {
  id: number;
  longName: string;
  shortName: string;
  displayOrder: number;
};

type GroupedJobType = {
  id: number;
  name: string;
};

export type RecruitmentType = {
  key: string;
  label: string;
};

type Props = {
  type: RecruitmentType;
  tags: Tag[];
  targetYear: number;
  groupedJobTypes: GroupedJobType[];
  size?: 'sm' | 'md';
  isGrayColor?: boolean;
  hasTargetYear?: boolean;
  lineClampOne?: boolean;
};

const TYPE_KEY_INTERN = 'intern';
const TYPE_KEY_SELECTION = 'selection';
const TYPE_KEY_EVENT = 'event';
const TYPE_KEY_JOINT_EVENT = 'jointEvent';
const TYPE_KEY_GSSKT_EVENT = 'gssktEvent';

export const Tags: React.FC<Props> = ({
  tags,
  type,
  targetYear,
  groupedJobTypes,
  size = 'md',
  isGrayColor,
  hasTargetYear = true,
  lineClampOne,
}) => {
  const baseTagStyle = cn(
    'px-8 py-[2px] text-xs min-w-[72px] text-center leading-4 rounded-12',
    'inline-block pc:mr-8 mr-4 pc:mb-4',
    size === 'md' && 'pc:py-4 pc:text-sm',
    isGrayColor && 'text-black-3 border-black-3',
  );

  const renderLabels = () => {
    const typeToColor: Record<string, string> = {
      [TYPE_KEY_INTERN]: 'bg-category-7 border border-category-7 text-white',
      [TYPE_KEY_SELECTION]: 'bg-category-1 border border-category-1 text-white',
      [TYPE_KEY_EVENT]: 'bg-category-3 border border-category-3 text-white',
      [TYPE_KEY_JOINT_EVENT]:
        'bg-category-3 border border-category-3 text-white',
      [TYPE_KEY_GSSKT_EVENT]:
        'bg-category-3 border border-category-3 text-white',
    };

    if (type && typeToColor[type.key]) {
      return (
        <span className={cn(baseTagStyle, typeToColor[type.key])}>
          {type.label === TYPE_KEY_GSSKT_EVENT ? '外資就活主催' : type.label}
        </span>
      );
    }
  };

  const renderTargetYear = () =>
    hasTargetYear && (
      <span className={cn(baseTagStyle, 'border border-black-2')}>
        {targetYear > 0 ? `${targetYear}卒` : '学年不問'}
      </span>
    );

  const renderTags = () => {
    if (tags.length > 0) {
      return tags
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((tag) => (
          <span
            className={cn(baseTagStyle, 'border border-black-2')}
            key={tag.id}
          >
            {tag.shortName}
          </span>
        ));
    }
  };

  const renderGroupedJobTypes = () => {
    if (groupedJobTypes.length > 0) {
      return groupedJobTypes.map((jobType) => (
        <span
          className={cn(baseTagStyle, 'border border-black-2')}
          key={jobType.id}
        >
          {jobType.name}
        </span>
      ));
    }
  };

  return (
    <div
      className={cn('pc:line-clamp-1', {
        'line-clamp-1': lineClampOne,
      })}
    >
      {renderLabels()}
      {renderTargetYear()}
      {renderTags()}
      {renderGroupedJobTypes()}
    </div>
  );
};
